import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building2, ArrowLeft, User, Calendar, TrendingUp, Award, Clock, Target, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { projectService } from '../../services/projectService';
import { phaseService } from '../../services/phaseService';
import { userService } from '../../services/userService';
import { supabase } from '../../lib/supabase';
import HorizontalTimeline from '../client-project-view/components/HorizontalTimeline';

const ClientProjectViewAdmin = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const { user, profile } = useAuth();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [phases, setPhases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (profile?.role !== 'admin') {
      navigate('/admin');
      return;
    }
    loadClientData();
  }, [clientId, profile, navigate]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      
      // Load client info
      const clientData = await userService?.getUserById(clientId);
      setClient(clientData);
      
      // Load client's projects
      const projectsData = await projectService?.getProjectsByClient(clientId);
      setProjects(projectsData);
      
      if (projectsData?.length > 0) {
        setSelectedProject(projectsData?.[0]);
        await loadPhases(projectsData?.[0]?.id);
      }
      
      setError('');
    } catch (err) {
      setError('Erro ao carregar dados do cliente: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPhases = async (projectId) => {
    try {
      const data = await phaseService?.getPhasesByProject(projectId);
      setPhases(data);
    } catch (err) {
      setError('Erro ao carregar fases: ' + err?.message);
    }
  };

  // Real-time subscription for phase changes
  useEffect(() => {
    if (!selectedProject?.id) return;

    // Subscribe to changes in project_phases table for current project
    const channel = supabase
      ?.channel(`admin-project-phases-${selectedProject?.id}`)
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_phases',
          filter: `project_id=eq.${selectedProject?.id}`
        },
        (payload) => {
          console.log('Real-time phase update (admin view):', payload);
          
          // Handle different event types
          if (payload?.eventType === 'INSERT') {
            // New phase added - convert to camelCase
            const newPhase = {
              id: payload?.new?.id,
              projectId: payload?.new?.project_id,
              name: payload?.new?.name,
              description: payload?.new?.description,
              phaseOrder: payload?.new?.phase_order,
              startDate: payload?.new?.start_date,
              estimatedEndDate: payload?.new?.estimated_end_date,
              actualEndDate: payload?.new?.actual_end_date,
              status: payload?.new?.status,
              progressPercentage: payload?.new?.progress_percentage,
              createdAt: payload?.new?.created_at,
              updatedAt: payload?.new?.updated_at
            };
            setPhases(prev => [...prev, newPhase]?.sort((a, b) => a?.phaseOrder - b?.phaseOrder));
          } else if (payload?.eventType === 'UPDATE') {
            // Phase updated - convert to camelCase
            const updatedPhase = {
              id: payload?.new?.id,
              projectId: payload?.new?.project_id,
              name: payload?.new?.name,
              description: payload?.new?.description,
              phaseOrder: payload?.new?.phase_order,
              startDate: payload?.new?.start_date,
              estimatedEndDate: payload?.new?.estimated_end_date,
              actualEndDate: payload?.new?.actual_end_date,
              status: payload?.new?.status,
              progressPercentage: payload?.new?.progress_percentage,
              createdAt: payload?.new?.created_at,
              updatedAt: payload?.new?.updated_at
            };
            setPhases(prev => prev?.map(phase => 
              phase?.id === updatedPhase?.id ? updatedPhase : phase
            ));
          } else if (payload?.eventType === 'DELETE') {
            // Phase deleted
            setPhases(prev => prev?.filter(phase => phase?.id !== payload?.old?.id));
          }
        }
      )
      ?.subscribe();

    // Cleanup subscription on unmount or project change
    return () => {
      supabase?.removeChannel(channel);
    };
  }, [selectedProject?.id]);

  const handleProjectSelect = async (project) => {
    setSelectedProject(project);
    await loadPhases(project?.id);
  };

  const calculateProjectStats = () => {
    if (!phases?.length) return { total: 0, completed: 0, inProgress: 0, overall: 0 };
    
    const total = phases?.length;
    const completed = phases?.filter(p => p?.status === 'concluido')?.length;
    const inProgress = phases?.filter(p => p?.status === 'em_andamento')?.length;
    const overall = Math.round((completed / total) * 100);
    
    return { total, completed, inProgress, overall };
  };

  const stats = calculateProjectStats();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-indigo-200 animate-pulse"></div>
            <div className="absolute inset-0 rounded-full border-t-4 border-indigo-600 animate-spin"></div>
          </div>
          <p className="text-base sm:text-lg font-medium text-slate-700">Carregando dados do cliente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Read-Only Admin Header Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5" />
            <div>
              <p className="text-sm font-bold">Modo Visualização Administrativa (Somente Leitura)</p>
              <p className="text-xs opacity-90">Visualizando como: {client?.fullName || client?.email}</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-semibold"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Voltar ao Admin</span>
          </button>
        </div>
      </div>

      {/* Client Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-5">
          <div className="flex items-center gap-3">
            <div className="p-2 sm:p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
              <User className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-800">
                {client?.fullName || 'Cliente'}
              </h1>
              <p className="text-sm text-slate-600">{client?.email}</p>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 sm:py-10 space-y-6 sm:space-y-8">
        {projects?.length === 0 ? (
          <div className="text-center py-20 bg-white/60 backdrop-blur-sm border border-slate-200 rounded-3xl shadow-xl">
            <Building2 className="h-16 w-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-800 mb-3">Nenhum projeto atribuído</h2>
            <p className="text-slate-600">Este cliente ainda não possui projetos cadastrados</p>
          </div>
        ) : (
          <div className="space-y-6 sm:space-y-8">
            {/* Project Selector */}
            {projects?.length > 1 && (
              <div className="bg-white/60 backdrop-blur-sm border border-slate-200 rounded-3xl p-6 shadow-lg">
                <label className="flex items-center gap-2 text-base font-bold text-slate-800 mb-4">
                  <Target className="h-5 w-5 text-indigo-600" />
                  Selecione o Projeto
                </label>
                <select
                  value={selectedProject?.id || ''}
                  onChange={(e) => {
                    const project = projects?.find(p => p?.id === e?.target?.value);
                    if (project) handleProjectSelect(project);
                  }}
                  className="w-full px-5 py-4 bg-white border-2 border-slate-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 focus:border-indigo-400 text-base text-slate-800 font-medium shadow-sm"
                >
                  {projects?.map((project) => (
                    <option key={project?.id} value={project?.id}>
                      {project?.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Project Info Card */}
            {selectedProject && (
              <div className="bg-white/60 backdrop-blur-sm border border-slate-200 rounded-3xl p-8 shadow-xl">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                        <Building2 className="h-6 w-6 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-slate-800">{selectedProject?.name}</h2>
                    </div>
                    {selectedProject?.description && (
                      <p className="text-lg text-slate-600 leading-relaxed">{selectedProject?.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 px-5 py-3 bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-700 rounded-2xl text-base font-bold border border-emerald-200 shadow-md">
                    <TrendingUp className="h-5 w-5" />
                    {selectedProject?.status}
                  </div>
                </div>

                {/* Project Statistics */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100 shadow-md">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                        <Target className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-sm font-semibold text-slate-600">Total de Fases</span>
                    </div>
                    <p className="text-3xl font-bold text-indigo-600">{stats?.total}</p>
                  </div>

                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100 shadow-md">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
                        <Award className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-sm font-semibold text-slate-600">Concluídas</span>
                    </div>
                    <p className="text-3xl font-bold text-emerald-600">{stats?.completed}</p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-100 shadow-md">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl">
                        <Clock className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-sm font-semibold text-slate-600">Em Andamento</span>
                    </div>
                    <p className="text-3xl font-bold text-blue-600">{stats?.inProgress}</p>
                  </div>

                  <div className="bg-gradient-to-br from-violet-50 to-fuchsia-50 rounded-2xl p-6 border border-violet-100 shadow-md">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-gradient-to-br from-violet-500 to-fuchsia-600 rounded-xl">
                        <TrendingUp className="h-5 w-5 text-white" />
                      </div>
                      <span className="text-sm font-semibold text-slate-600">Progresso Total</span>
                    </div>
                    <p className="text-3xl font-bold text-violet-600">{stats?.overall}%</p>
                  </div>
                </div>

                {/* Timeline Section */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                      <Calendar className="h-6 w-6 text-indigo-600" />
                      Cronograma de Execução
                    </h3>
                    <div className="px-4 py-2 bg-amber-100 text-amber-800 rounded-lg text-sm font-semibold border border-amber-200">
                      Somente Leitura
                    </div>
                  </div>
                  
                  {phases?.length > 0 ? (
                    <HorizontalTimeline phases={phases} />
                  ) : (
                    <div className="text-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300">
                      <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-slate-600 font-medium">Nenhuma fase cadastrada para este projeto</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientProjectViewAdmin;